import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  categoryList,
  formatViews,
  getMoviesByCategory,
  regions,
  resolveCategory,
  years,
} from '@/lib/movies';

type SortKey = 'updated' | 'views' | 'rating';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'updated', label: '最近更新' },
  { key: 'views', label: '点击量' },
  { key: 'rating', label: '评分' },
];

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type } = useParams();
  const meta = resolveCategory(type);
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState<SortKey>('updated');
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  usePageMeta({
    title: meta ? `${meta.h1} - av免费网站` : '分类不存在 - av免费网站',
    description: meta
      ? `${meta.description} 共收录 ${getMoviesByCategory(meta.key).length} 部虚构示例影片，支持按地区、年份筛选与多种排序。`
      : '该分类不存在或已下架，请返回首页继续浏览其他影片分类。',
    keywords: meta ? `${meta.name}AV影片,${meta.name}高清影片,AV在线观看,av免费网站` : 'av免费网站',
  });

  const list = useMemo(() => {
    if (!meta) return [];
    const filtered = getMoviesByCategory(meta.key).filter((movie) => {
      const regionMatch = region === '全部' || movie.region === region;
      let yearMatch = true;
      if (year === '更早') yearMatch = movie.year < 2023;
      else if (year !== '全部') yearMatch = movie.year === Number(year);
      return regionMatch && yearMatch;
    });

    return filtered.sort((a, b) => {
      if (sort === 'views') return b.views - a.views;
      if (sort === 'rating') return b.rating - a.rating;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [meta, region, year, sort]);

  const categoryTotal = meta ? getMoviesByCategory(meta.key) : [];
  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const hasFilter = region !== '全部' || year !== '全部';

  const goPage = (next: number) => {
    setSearchParams(next === 1 ? {} : { page: String(next) });
    window.scrollTo({ top: 260, behavior: 'smooth' });
  };

  const resetFilters = () => {
    setRegion('全部');
    setYear('全部');
    setSort('updated');
    setSearchParams({});
  };

  const changeFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setSearchParams({});
  };

  if (!meta) {
    return (
      <div className="max-w-page mx-auto px-4 md:px-8 py-24 md:py-32 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-500 flex items-center justify-center">
          <i className="ri-folder-unknow-line text-3xl" />
        </span>
        <h1 className="mt-6 text-2xl md:text-3xl font-bold">这个分类不存在</h1>
        <p className="mt-3 text-sm text-foreground-600 max-w-md mx-auto">
          链接可能有误，或者该分类已经调整。你可以从下面的入口重新开始浏览。
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          {categoryList.map((item) => (
            <Link
              key={item.key}
              to={`/category/${item.slug}`}
              className="h-11 px-5 rounded-md border border-background-300/70 text-sm text-foreground-800 hover:border-primary-500/60 hover:text-primary-400 transition-colors flex items-center cursor-pointer whitespace-nowrap"
            >
              {item.name}影片
            </Link>
          ))}
          <Link
            to="/"
            className="h-11 px-5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium flex items-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-background-200/70 bg-background-100">
        <div className="max-w-page mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-8 md:pb-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
            <span>分类</span>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
            <span className="text-foreground-800">{meta.name}影片</span>
          </nav>

          <div className="mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">
                Category / {meta.key}
              </p>
              <h1 className="mt-2 text-2xl md:text-4xl font-black tracking-tight text-foreground-950">
                {meta.h1}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{meta.description}</p>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <p className="text-[11px] text-foreground-500">收录影片</p>
                <p className="mt-1 font-label text-2xl text-foreground-950">{categoryTotal.length}</p>
              </div>
              <div className="w-px h-10 bg-background-300/60" />
              <div>
                <p className="text-[11px] text-foreground-500">累计浏览</p>
                <p className="mt-1 font-label text-2xl text-primary-400">
                  {formatViews(categoryTotal.reduce((sum, movie) => sum + movie.views, 0))}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-2">
            {categoryList.map((item) => (
              <Link
                key={item.key}
                to={`/category/${item.slug}`}
                className={`h-9 px-4 rounded-full text-sm flex items-center transition-colors cursor-pointer whitespace-nowrap ${
                  item.key === meta.key
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950 font-medium'
                    : 'border border-background-300/70 text-foreground-700 hover:border-primary-500/60 hover:text-foreground-950'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-8 py-7 md:py-10">
        <div className="rounded-lg border border-background-300/60 bg-background-100 p-4 md:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-16 shrink-0 text-xs text-foreground-500">地区</span>
                {regions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => changeFilter(setRegion)(item)}
                    className={`h-8 px-3 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      region === item
                        ? 'bg-secondary-100 text-secondary-900 border border-secondary-300/50'
                        : 'border border-transparent text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-16 shrink-0 text-xs text-foreground-500">年份</span>
                {years.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => changeFilter(setYear)(item)}
                    className={`h-8 px-3 rounded-md text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      year === item
                        ? 'bg-secondary-100 text-secondary-900 border border-secondary-300/50'
                        : 'border border-transparent text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <span className="text-xs text-foreground-500 mr-1">排序</span>
              <div className="flex items-center p-1 rounded-full bg-background-200/70">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setSearchParams({});
                    }}
                    className={`h-8 px-3.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      sort === option.key
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950 font-medium'
                        : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {hasFilter && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="h-8 px-3 rounded-md text-xs text-accent-300 border border-accent-500/30 hover:bg-accent-500/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  重置筛选
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <p className="text-xs text-foreground-500">
            共 <span className="text-foreground-900">{list.length}</span> 部影片 · 第 {currentPage} / {totalPages} 页
          </p>
          <p className="hidden sm:block text-xs text-foreground-500">所有影片资料均为虚构示例</p>
        </div>

        {visible.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {visible.map((movie, index) => (
                <div key={movie.id} style={{ animationDelay: `${index * 35}ms` }}>
                  <MovieCard movie={movie} />
                </div>
              ))}
            </div>
            <div className="mt-10 md:mt-12">
              <Pagination page={currentPage} totalPages={totalPages} onChange={goPage} />
            </div>
          </>
        ) : (
          <div className="mt-6 rounded-lg border border-background-300/60 bg-background-100 py-16 md:py-20 px-6 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
              <i className="ri-filter-off-line text-3xl" />
            </span>
            <h2 className="mt-5 text-lg md:text-xl font-bold">没有符合条件的影片</h2>
            <p className="mt-2 text-sm text-foreground-600">
              当前筛选组合下暂无内容，试着放宽地区或年份条件。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 h-11 px-6 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </section>
    </div>
  );
}